import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { sendWelcomeEmail } from "@/api/vercel";
import "@/styles/_swiper.scss";
import "@/styles/lexi-pro.scss";
import ReCAPTCHA from "react-google-recaptcha";
import "swiper/css";
import "swiper/css/pagination";
import { A11y, Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { cn } from "@/lib/utils";
import { completeSignup } from "@/utils/signup";
import {
  trackCompleteRegistration,
  trackViewContent,
} from "@/utils/facebook-pixel-events";
import {
  LEXI_PRO_VARIANTS,
  trackLexiProCtaClick,
} from "@/utils/lexi-pro-split";
import logger from "@/utils/logger";
import { raceDayTipsTime } from "@/utils/time-format";
import { getStripeUrlBasedOnEnvironment } from "@/utils/vars";
import {
  AlertCircle,
  Check,
  ChevronUp,
  ChevronsUp,
  Circle,
  Clock3,
  Loader2,
  MessageCircle,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import {
  LEXI_GOOGLE_REVIEWS,
  LexiGoogleReviewsSection,
  LexiGoogleReviewsSocialProofStrip,
} from "@/components/LexiGoogleReviews";
import CustomMarquee from "@/components/custom-marquee";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import Footer from "@/pages/landing/footer";

// reCAPTCHA site keys
const RECAPTCHA_SCORE_KEY =
  import.meta.env.VITE_RECAPTCHA_SITE_KEY ||
  "6LdewqYrAAAAACyovzEoE3tUtmtNi4ZgLTnqzTnn";
// reCAPTCHA v2 Checkbox key for fallback when v3 score is low
const RECAPTCHA_CHECKBOX_KEY =
  import.meta.env.VITE_RECAPTCHA_CHECKBOX_KEY ||
  "6Lexl10sAAAAAMFs_DawLbU7xGg9ogJcgHy3dWDH";

// Utility functions
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

function ErrorMessage({ message, type = "field" }) {
  return (
    <div className="flex items-center gap-2">
      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
      <p
        className={
          type === "field" ? "text-xs text-red-600" : "text-sm text-red-600"
        }
      >
        {message}
      </p>
    </div>
  );
}

function LexiProCtaButton({ children, onClick, className }) {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await onClick?.();
    } finally {
      window.setTimeout(() => setIsLoading(false), 650);
    }
  }, [isLoading, onClick]);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-busy={isLoading}
      className={[
        "lexi-pro-cta",
        "relative inline-flex items-center justify-center rounded-full",
        "bg-brand-pink text-white font-[500]",
        "px-6 py-3",
        // "shadow-[0_10px_25px_rgba(243,0,153,0.25)]",
        "transition-transform duration-150 ease-out active:scale-[0.98]",
        "hover:scale-[1.02]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-pink/40",
        className || "",
      ].join(" ")}
    >
      <span className={isLoading ? "opacity-0" : "opacity-100"}>
        {children}
      </span>
      <span className="absolute inset-0 grid place-items-center">
        <Spinner size="small" show={isLoading} className="text-white" />
      </span>
    </button>
  );
}

export default function LexiProLandingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const reviewsRef = useRef(null);
  const emailInputRef = useRef(null);
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);

  // reCAPTCHA refs
  const recaptchaScoreRef = useRef(null);
  const recaptchaChallengeRef = useRef(null);
  const [showChallenge, setShowChallenge] = useState(false);

  const [overlayEnabled, setOverlayEnabled] = useState(() => {
    try {
      return new URLSearchParams(location.search).has("overlay");
    } catch {
      return false;
    }
  });

  const setOverlayParam = useCallback(
    (nextEnabled) => {
      const sp = new URLSearchParams(location.search);
      if (nextEnabled) sp.set("overlay", "1");
      else sp.delete("overlay");

      const nextSearch = sp.toString();
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: true },
      );
    },
    [location.pathname, location.search, navigate],
  );

  // Keep state in sync if user edits URL manually or navigates.
  useEffect(() => {
    try {
      setOverlayEnabled(new URLSearchParams(location.search).has("overlay"));
    } catch {
      setOverlayEnabled(false);
    }
  }, [location.search]);

  const hasTrackedViewRef = useRef(false);

  useEffect(() => {
    if (!hasTrackedViewRef.current) {
      trackViewContent({
        content_name: "Lexi Pro Page",
        content_category: "Subscription",
      });
      hasTrackedViewRef.current = true;
    }
  }, []);

  // Keyboard shortcut: Shift+W toggles overlay (ignored while typing in inputs).
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.repeat) return;

      const target = e.target;
      const isTypingTarget =
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT");

      if (isTypingTarget) return;

      const isToggleOverlay =
        e.shiftKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.metaKey &&
        e.code === "KeyW";
      if (!isToggleOverlay) return;

      e.preventDefault();
      setOverlayEnabled((prev) => {
        const next = !prev;
        setOverlayParam(next);
        return next;
      });
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [setOverlayParam]);

  const galleryImages = useMemo(
    () => [
      {
        src: "/images/gallery/lexi-pro/slide-1-bg-tny.jpg",
        desktopSrc: "/images/gallery/lexi-pro/slide-1-desktop-bg-tny.jpg",
        overlay: "/images/gallery/lexi-pro/slide-1-iphone-tny.png",
        caption:
          "<span style='font-weight: 700;'>Early Mail</span> • Up to 3 Days Out",
      },
      {
        src: "/images/gallery/lexi-pro/slide-2-bg-tny.jpg",
        desktopSrc: "/images/gallery/lexi-pro/slide-2-desktop-bg-tny.jpg",
        overlay: "/images/gallery/lexi-pro/slide-2-iphone-tny.png",
        caption:
          `<span style='font-weight: 700;'>Race Day Tips</span> by ${raceDayTipsTime()}`,
      },
      {
        src: "/images/gallery/lexi-pro/slide-3-bg-tny.jpg",
        desktopSrc: "/images/gallery/lexi-pro/slide-3-desktop-bg-tny.jpg",
        overlay: "/images/gallery/lexi-pro/slide-3-iphone-tny.png",
        caption:
          "<span style='font-weight: 700;'>Late Mail</span> • Minutes Before Jump",
      },
      {
        src: "/images/gallery/lexi-pro/slide-4-bg-tny.jpg",
        desktopSrc: "/images/gallery/lexi-pro/slide-4-desktop-bg-tny.jpg",
        overlay: "/images/gallery/lexi-pro/slide-4-iphone-tny.png",
        caption:
          "<span style='font-weight: 700;'>Ask Lexi</span> about any runner, anytime",
      },
    ],
    [],
  );

  const earlyMailGallery = useMemo(
    () => [
      {
        src: "/images/gallery/early-mail/lexi-early-mail-1.png",
        alt: "Early mail example 1",
      },
      {
        src: "/images/gallery/early-mail/lexi-early-mail-2.png",
        alt: "Early mail example 2",
      },
      {
        src: "/images/gallery/early-mail/lexi-early-mail-3.png",
        alt: "Early mail example 3",
      },
      {
        src: "/images/gallery/early-mail/lexi-early-mail-4.png",
        alt: "Early mail example 4",
      },
      {
        src: "/images/gallery/early-mail/lexi-early-mail-5.png",
        alt: "Early mail example 5",
      },
    ],
    [],
  );

  const raceDayGallery = useMemo(
    () => [
      {
        src: "/images/gallery/race-day/lexi-race-day-1-tny.png",
        alt: "Race day tips example 1",
      },
      {
        src: "/images/gallery/race-day/lexi-race-day-2-tny.png",
        alt: "Race day tips example 2",
      },
      {
        src: "/images/gallery/race-day/lexi-race-day-3-tny.png",
        alt: "Race day tips example 3",
      },
    ],
    [],
  );

  const lateMailGallery = useMemo(
    () => [
      {
        src: "/images/gallery/late-mail/lexi-late-mail-1.png",
        alt: "Late mail tips example 1",
      },
      {
        src: "/images/gallery/late-mail/lexi-late-mail-2.png",
        alt: "Late mail tips example 2",
      },
      {
        src: "/images/gallery/late-mail/lexi-late-mail-3.png",
        alt: "Late mail tips example 3",
      },
      {
        src: "/images/gallery/late-mail/lexi-late-mail-4.png",
        alt: "Late mail tips example 4",
      },
    ],
    [],
  );

  const bulletIcons = useMemo(
    () => ({
      circle: <Circle className="h-4 w-4 text-[#9ca3af]" aria-hidden="true" />,
      chevronsUp: (
        <ChevronsUp className="h-4 w-4 text-emerald-600" aria-hidden="true" />
      ),
      chevronUp: (
        <ChevronUp className="h-4 w-4 text-emerald-600" aria-hidden="true" />
      ),
    }),
    [],
  );

  const scrollToReviews = useCallback(() => {
    console.log("[LexiPro] scrollToReviews called");
    const el = reviewsRef.current || document.getElementById("reviews");
    console.log("[LexiPro] reviews element:", el);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const targetY = rect.top + scrollTop - 12;
    console.log("[LexiPro] scrolling to Y:", targetY);
    window.scrollTo({ top: targetY, behavior: "smooth" });
  }, []);

  const focusEmailField = useCallback(() => {
    const el = emailInputRef.current;
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }, []);

  const goToLexiProPaywall = useCallback(() => {
    // Use the same Pro paywall destination as the existing navbar "Lexi Pro" action.
    window.location.href = getStripeUrlBasedOnEnvironment();
  }, []);

  const scrollToSignupForm = useCallback(() => {
    // 50/50 randomization condition
    if (Math.random() < 0.5) {
      trackLexiProCtaClick(LEXI_PRO_VARIANTS.PAYMENT_LINK);
      goToLexiProPaywall();
      return;
    }

    trackLexiProCtaClick(LEXI_PRO_VARIANTS.SIGNUP_FORM);

    // Show form before scrolling
    setFormVisible(true);

    // Minor delay to ensure component is rendered before measuring scroll position
    window.setTimeout(() => {
      const el = formRef.current || document.getElementById("signup");
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Wait a beat so focus doesn't fight smooth scrolling.
      window.setTimeout(() => focusEmailField(), 450);
    }, 50);
  }, [focusEmailField, goToLexiProPaywall]);

  const onSubmitSignup = useCallback(
    async (e) => {
      e.preventDefault();

      // Validate form
      const newErrors = {};

      if (!email) {
        newErrors.email = "Email is required";
      } else if (!validateEmail(email)) {
        newErrors.email = "Please enter a valid email address";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      setLoading(true);

      try {
        // Get reCAPTCHA token for bot protection (mandatory)
        let recaptchaToken = null;
        try {
          if (showChallenge) {
            // Checkbox mode - reset the invisible one first
            if (recaptchaScoreRef.current) {
              try {
                recaptchaScoreRef.current.reset();
              } catch (e) {
                // Ignore reset errors
              }
            }

            if (recaptchaChallengeRef.current) {
              recaptchaToken = recaptchaChallengeRef.current.getValue();

              if (!recaptchaToken) {
                setErrors({ submit: "Please complete the security check." });
                setLoading(false);
                return;
              }
            }
          } else {
            // Score mode (Invisible)
            if (recaptchaScoreRef.current) {
              recaptchaToken = await recaptchaScoreRef.current.executeAsync();
              recaptchaScoreRef.current.reset();
            }
          }
        } catch (recaptchaError) {
          logger.error("reCAPTCHA error:", recaptchaError);
          setLoading(false);
          setErrors({
            submit: "Security verification failed. Please try again.",
          });
          return;
        }

        // Enforce reCAPTCHA token requirement
        if (!recaptchaToken) {
          setLoading(false);
          setErrors({
            submit: showChallenge
              ? "Please complete the security check."
              : "Security verification is required. Please enable JavaScript and try again.",
          });
          return;
        }


        const response = await fetch("/api/create-prospect", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email.toLowerCase(),
            disabledOtp: true,
            with: "auth",
            recaptchaToken, // Include reCAPTCHA token for bot protection
            // Send the key we used so the backend validates against the correct one
            siteKey: showChallenge
              ? RECAPTCHA_CHECKBOX_KEY
              : RECAPTCHA_SCORE_KEY,
          }),
        });

        const result = await response.json();

        if (result.success && result.token) {
          logger.log("Prospect created successfully with auth:", result);
          trackCompleteRegistration();
          // Send welcome email to the user
          sendWelcomeEmail({
            email: email.toLowerCase(),
          }).catch((error) => {
            logger.error("Failed to send welcome email:", error);
            // Don't block the flow if email fails
          });

          const destination = await completeSignup(result, "pro");
          setLoading(false);
          navigate(destination, { replace: true });
        } else {
          // Handle "requires_challenge" error
          if (result.error === "requires_challenge") {
            logger.warn("Low reCAPTCHA score, requiring visible challenge");
            setLoading(false);
            setShowChallenge(true); // Switch to visible mode
            setErrors({
              submit: "Please complete the security check below to continue.",
            });
            return;
          }

          setErrors({ submit: result.error || "Failed to create account" });
          setLoading(false);
        }
      } catch (error) {
        logger.error("Error creating prospect:", error);
        setErrors({ submit: "An error occurred. Please try again." });
        setLoading(false);
      }
    },
    [email, navigate],
  );

  const isFormValid = useMemo(
    () => validateEmail(email),
    [email],
  );

  // Figma frame width: 430px (iPhone 14/15 Pro Max)
  const frameWidthClass = "w-[430px] max-w-full";

  // Shared gallery swiper component
  const GallerySwiper = ({ className = "", useDesktopImages = false }) => (
    <Swiper
      modules={[Autoplay, Pagination, A11y]}
      loop
      grabCursor
      autoplay={{
        delay: 5000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }}
      pagination={{ clickable: true }}
      a11y={{ enabled: true }}
      className={`lexi-pro-swiper h-full w-full !pb-[28px] ${className}`}
    >
      {galleryImages.map((slide) => (
        <SwiperSlide key={slide.src}>
          <div className="lexi-pro-swiper__caption absolute left-0 top-0 h-[50px] xl:h-[56px] w-full bg-brand-pink z-20 flex items-center justify-center">
            <p className="text-white text-[16px] xsm:text-[20px] xl:text-[22px] font-[500] leading-[29px] text-center">
              <span
                className="lexi-pro-swiper__captionText inline-block"
                dangerouslySetInnerHTML={{ __html: slide.caption }}
              />
            </p>
          </div>
          <div className="lexi-pro-swiper__slide relative h-full w-full">


          {slide.overlay ? (
              <img
                src={slide.overlay}
                alt=""
                aria-hidden="true"
                className="lexi-pro-swiper__overlay z-20"
                loading="lazy"
              />
            ) : null}


            {/* Dark gradient overlay for text contrast */}
            <div className="hidden md:block absolute inset-0 bg-gradient-to-l from-black/60 via-black/40 to-transparent z-10 pointer-events-none" />

            <img
              src={
                useDesktopImages && slide.desktopSrc
                  ? slide.desktopSrc
                  : slide.src
              }
              alt={slide.caption.replace(/<[^>]*>?/g, "").trim()}
              className="h-full w-full object-cover object-top"
              loading="lazy"
            />
            
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );

  // Desktop hero tabs component
  const HeroTabs = ({ className = "", isDesktop = false }) => (
    <div className={className}>
      <div
        className={`bg-white border-2 border-brand-pink rounded-[10px] shadow-[0_0_2px_rgba(0,0,0,0.25)] ${isDesktop ? "w-[160px] h-[100px]" : "w-[129px] h-[83px]"} relative`}
      >
        <Clock3
          className={`absolute left-1/2 -translate-x-1/2 text-brand-pink ${isDesktop ? "top-[16px] h-[26px] w-[26px]" : "top-[12px] h-[22px] w-[22px]"}`}
          aria-hidden="true"
        />
        <div
          className={`absolute left-1/2 -translate-x-1/2 text-center font-bold tracking-[0.1px] text-black ${isDesktop ? "top-[48px] w-[140px] text-[12px]" : "top-[40px] w-[110px] text-[10px]"}`}
        >
          12 Tips Per Race
        </div>
        <div
          className={`absolute left-1/2 -translate-x-1/2 text-center font-medium tracking-[0.08px] text-black ${isDesktop ? "top-[68px] w-[140px] text-[10px]" : "top-[58px] w-[110px] text-[8px]"}`}
        >
          Early • Race Day • Late Mail
        </div>
      </div>

      <div
        className={`bg-white border-2 border-brand-pink rounded-[10px] shadow-[0_0_2px_rgba(0,0,0,0.25)] ${isDesktop ? "w-[160px] h-[100px]" : "w-[129px] h-[83px]"} relative`}
      >
        <Sparkles
          className={`absolute left-1/2 -translate-x-1/2 text-brand-pink ${isDesktop ? "top-[16px] h-[24px] w-[24px]" : "top-[12px] h-[20px] w-[20px]"}`}
          aria-hidden="true"
        />
        <div
          className={`absolute left-1/2 -translate-x-1/2 text-center font-bold tracking-[0.1px] text-black ${isDesktop ? "top-[48px] w-[140px] text-[12px]" : "top-[40px] w-[110px] text-[10px]"}`}
        >
          AI-Generated Tips
        </div>
        <div
          className={`absolute left-1/2 -translate-x-1/2 text-center font-medium tracking-[0.08px] text-black ${isDesktop ? "top-[68px] w-[140px] text-[10px]" : "top-[58px] w-[110px] text-[8px]"}`}
        >
          Form · Markets · History
        </div>
      </div>

      <div
        className={`bg-white border-2 border-brand-pink rounded-[10px] shadow-[0_0_2px_rgba(0,0,0,0.25)] ${isDesktop ? "w-[160px] h-[100px]" : "w-[129px] h-[83px]"} relative`}
      >
        <MessageCircle
          className={`absolute left-1/2 -translate-x-1/2 text-brand-pink ${isDesktop ? "top-[16px] h-[26px] w-[26px]" : "top-[12px] h-[22px] w-[22px]"}`}
          aria-hidden="true"
        />
        <div
          className={`absolute left-1/2 -translate-x-1/2 text-center font-bold tracking-[0.1px] text-black ${isDesktop ? "top-[48px] w-[140px] text-[12px]" : "top-[40px] w-[110px] text-[10px]"}`}
        >
          Ask Lexi AI
        </div>
        <div
          className={`absolute left-1/2 -translate-x-1/2 text-center font-medium tracking-[0.08px] text-black ${isDesktop ? "top-[68px] w-[140px] text-[10px]" : "top-[58px] w-[110px] text-[8px]"}`}
        >
          Chat + full results history
        </div>
      </div>
    </div>
  );

  return (
    <div className="lexi-pro-page min-h-screen bg-white relative overflow-x-hidden">
      {overlayEnabled ? (
        <div className="absolute inset-x-0 top-0 z-50 pointer-events-none select-none flex justify-center xl:hidden">
          <img
            src="/images/lexi-pro-mobile-hero.png"
            alt=""
            aria-hidden="true"
            className="w-[430px] max-w-full h-auto opacity-60"
          />
        </div>
      ) : null}

      {/* ============================================
          MOBILE HERO (< 1280px)
          ============================================ */}
      <div className="w-full flex justify-center xl:hidden">
        <div className={`${frameWidthClass} relative`}>
          {/* VERY TOP marquee (Figma height: 52px) */}
          <div className="lexi-pro-marquee w-full relative">
            <CustomMarquee className="h-[52px] bg-[#FCE7F5]" />
          </div>

          {/* TOP logo + CTA (Figma height: 70px) */}
          <div className="relative flex items-center justify-between h-[70px] bg-white pl-[6px] pr-[10px]">
            <Link to="/" aria-label="Go to Lexi.tips home">
              <img
                src="/lexi-tips-logo.svg"
                alt="Lexi.tips"
                className="lexi-pro-logo relative w-full max-w-[165px] h-auto"
                loading="eager"
              />
            </Link>

            <LexiProCtaButton
              onClick={scrollToSignupForm}
              className={[
                "lexi-pro-top-cta",
                "relative h-[48px] w-auto rounded-[26px]",
                "border-4 border-[#FCE7F5]",
                "text-[12px] tracking-[0.12px] leading-[normal] font-[600]",
                "px-0 py-0",
              ].join(" ")}
            >
              Start Lexi Pro $29.95/month
            </LexiProCtaButton>
          </div>

          {/* GALLERY swiper js (Figma height: 426px from y=123 to y=549) */}
          <div className="relative w-full overflow-hidden">
            <div className="relative w-full inset-0 pt-[0px]">
              <GallerySwiper />
            </div>

            {/* fixed overlay tabs (Figma: y=457 => top 334 relative to this block) */}
            <HeroTabs className="lexi-pro-hero-tabs absolute left-[4px] xsm:left-[12px] top-[290px] xsm:top-[334px] z-30 flex gap-[6px] xsm:gap-[9px] pointer-events-none" />
          </div>

          {/* CONTENT (Figma height: 303px) */}
          <div className="relative bg-white pt-[0px] flex flex-col items-center">
            <h1 className="lexi-pro-hero-title text-center text-[30px] md:text-[37px] font-black leading-[36px] md:leading-[39px] tracking-[0.37px] text-black">
              Professional AI <br />
              Horse Racing Tips
            </h1>
            <p className="lexi-pro-hero-subtitle text-center text-[14px] md:text-[18px] font-medium leading-[22px] md:leading-[29px] text-[#444] pt-[18px] pb-[14px] px-[12px]">
              Early mail, race-day and late mail tips for <br />
              every track and race in Australia, every day!
            </p>

            <LexiProCtaButton
              onClick={scrollToSignupForm}
              className={[
                "lexi-pro-hero-cta",
                "border-[7px] border-[#FCE7F5] rounded-[33px]",
                "text-[18px] tracking-[0.32px] leading-[29px] font-[500]",
                "px-0 py-0",
              ].join(" ")}
            >
              Start Lexi Pro $29.95/month
            </LexiProCtaButton>

            <div className="flex items-center gap-2 pt-[6px]">
              <span className="relative h-[25px] w-[24px]" aria-hidden="true">
                <ShieldCheck
                  className="absolute inset-0 h-[25px] w-[24px]"
                  fill="#f30099"
                  stroke="#f30099"
                />
                <Check
                  className="absolute left-1/2 top-[52%] h-[14px] w-[14px] -translate-x-1/2 -translate-y-1/2 text-white"
                  strokeWidth={3}
                />
              </span>
              <span className="text-[14px] font-semibold leading-[29px] text-brand-pink">
                Cancel anytime
              </span>
            </div>
          </div>

          {/* SOCIAL PROOF (Figma height: 80px) */}
          <div className="relative h-[80px] bg-white">
            <LexiGoogleReviewsSocialProofStrip
              className="h-full w-full"
              maxChars={60}
              onReadMore={scrollToReviews}
              readMoreLabel="Read more"
            />
          </div>
        </div>
      </div>

      {/* ============================================
          DESKTOP HERO (1280px+)
          Full-width Swiper background with content overlay
          ============================================ */}
      <div className="hidden xl:block">
        {/* Desktop top bar with marquee */}
        <div className="lexi-pro-marquee w-full relative z-20">
          <CustomMarquee className="h-[52px] bg-[#FCE7F5]" />
        </div>

        {/* Desktop header - positioned over hero */}
        <div className="lexi-pro-desktop-header relative z-20">
          <div className="relative flex items-center justify-between h-[80px] bg-white/95 backdrop-blur-sm">
            <Link to="/" aria-label="Go to Lexi.tips home">
              <img
                src="/lexi-tips-logo.svg"
                alt="Lexi.tips"
                className="lexi-pro-logo relative w-full h-auto"
                loading="eager"
              />
            </Link>

            <LexiProCtaButton
              onClick={scrollToSignupForm}
              className={[
                "lexi-pro-top-cta",
                "relative w-auto rounded-[26px]",
                "border-4 border-[#FCE7F5]",
                "tracking-[0.12px] leading-[normal] font-[600]",
                "px-6",
              ].join(" ")}
            >
              Start Lexi Pro $29.95/month
            </LexiProCtaButton>
          </div>
        </div>

        {/* Desktop hero - full-width Swiper background with content overlay */}
        <div className="lexi-pro-desktop-hero--fullwidth relative">
          

          {/* Content overlay - centered container */}
          <div className="lexi-pro-desktop-hero__overlay relative z-20">
            <div className="max-w-[1200px] mx-auto pt-24 pb-8 py-16 min-h-[500px] flex items-center">
              <div className="grid grid-cols-2 gap-16 w-full items-center">
                {/* Left: Empty space for phone mockup visibility from slides */}
                <div className="relative" />

                {/* Right: Text content */}
                <div className="lexi-pro-desktop-hero__content">
                  <h1 className="lexi-pro-hero-title font-black tracking-[0.37px] text-white drop-shadow-lg">
                    Professional AI
                    <br />
                    Horse Racing Tips
                  </h1>
                  <p className="lexi-pro-hero-subtitle font-medium text-white/90 pt-[24px] pb-[20px] drop-shadow-md">
                    Early mail, race-day and late mail tips for
                    <br />
                    every track and race in Australia, every day!
                  </p>

                  <div className="flex flex-col items-start">
                    <LexiProCtaButton
                      onClick={scrollToSignupForm}
                      className={[
                        "lexi-pro-hero-cta",
                        "border-[7px] border-[#FCE7F5] rounded-[33px]",
                        "tracking-[0.32px] font-[500]",
                        "shadow-xl",
                      ].join(" ")}
                    >
                      Start Lexi Pro $29.95/month
                    </LexiProCtaButton>

                    <div className="flex items-center gap-2 pt-[10px] mx-auto">
                      <span
                        className="relative h-[25px] w-[24px]"
                        aria-hidden="true"
                      >
                        <ShieldCheck
                          className="absolute inset-0 h-[25px] w-[24px]"
                          fill="#f30099"
                          stroke="#f30099"
                        />
                        <Check
                          className="absolute left-1/2 top-[52%] h-[14px] w-[14px] -translate-x-1/2 -translate-y-1/2 text-white"
                          strokeWidth={3}
                        />
                      </span>
                      <span className="mx-auto text-[16px] font-semibold leading-[29px] text-white drop-shadow-sm">
                        Cancel anytime
                      </span>
                    </div>
                  </div>

                  {/* Desktop hero tabs */}
                  <div className="mt-10">
                    <HeroTabs className="flex gap-4" isDesktop={true} />
                  </div>
                </div>
                
              </div>
            </div>
          </div>

          {/* Full-width Swiper background layer */}
          <div className="lexi-pro-desktop-hero__swiper-bg absolute inset-0 w-full h-full overflow-hidden">
            <GallerySwiper className="!h-full" useDesktopImages={true} />
            
            {/* Dark gradient overlay for text contrast */}
            {/* <div className="absolute inset-0 bg-gradient-to-l from-black/60 via-black/40 to-transparent z-10 pointer-events-none" /> */}
          </div>


        </div>

        {/* Social proof strip below hero */}
        <div className="bg-white py-6">
          <div className="max-w-[800px] mx-auto px-10">
            <LexiGoogleReviewsSocialProofStrip
              className="h-[80px] w-full"
              maxChars={80}
              onReadMore={scrollToReviews}
              readMoreLabel="Read more"
            />
          </div>
        </div>
      </div>

      {/* BELOW THE FOLD */}
      <div className="lexi-pro-below-fold px-4 xl:px-0 pb-16">
        <div className="mx-auto w-full max-w-[430px] xl:max-w-[1200px] xl:px-10">
          {/* SIGN UP FORM (immediately below hero) */}
          {formVisible && (
            <section
              id="signup"
              ref={formRef}
              className="pt-8 xl:pt-16"
              aria-label="Lexi Pro signup"
            >
              <div className="lexi-pro-card rounded-2xl border border-gray-200 bg-white p-5 shadow-sm xl:max-w-[560px] xl:mx-auto">
                <h2 className="lexi-pro-section-title text-[24px] font-[800] text-black">
                  Get Full Lexi Pro Access
                </h2>
                <p className="lexi-pro-body mt-1 text-[13px] leading-relaxed text-gray-600">
                  Instant access to every track, every race, every day.
                </p>

                <form onSubmit={onSubmitSignup} className="mt-4 grid gap-3">
                  <div>
                    <label
                      htmlFor="lexi-pro-email"
                      className="block text-[12px] font-bold text-gray-700 mb-1"
                    >
                      Email <sup className="text-xs text-red-500">*</sup>
                    </label>
                    <Input
                      id="lexi-pro-email"
                      ref={emailInputRef}
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setErrors((prev) => ({ ...prev, email: "" }));
                      }}
                      onBlur={() => {
                        setErrors((prev) => {
                          if (!email)
                            return { ...prev, email: "Email is required" };
                          if (!validateEmail(email))
                            return {
                              ...prev,
                              email: "Please enter a valid email address",
                            };
                          return prev;
                        });
                      }}
                      className={cn("h-12", errors.email ? "border-red-500" : "")}
                    />
                    <p className="mt-1 text-[11px] text-gray-500">
                      Used for login and tip updates.
                    </p>
                    {errors.email && (
                      <div className="mt-2">
                        <ErrorMessage message={errors.email} type="field" />
                      </div>
                    )}
                  </div>



                  {errors.submit && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-3 items-center">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <p className="text-sm text-red-700 leading-snug">
                        {errors.submit}
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !isFormValid}
                    className={[
                      "mt-1 w-full rounded-[18px] py-3 font-[500]",
                      "bg-brand-pink text-white border-[7px] border-[#FCE7F5]",
                      "shadow-[0_10px_25px_rgba(243,0,153,0.25)]",
                      "transition-transform duration-150 ease-out active:scale-[0.98] hover:scale-[1.01]",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-pink/30",
                      "flex items-center justify-center gap-2",
                      "disabled:opacity-60 disabled:cursor-not-allowed",
                    ].join(" ")}
                  >
                    {loading ? (
                      <div className="flex w-full items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin inline-block" />
                        <span>Redirecting to checkout…</span>
                      </div>
                    ) : (
                      <div className="flex w-full items-center justify-center gap-3 font-[600]">
                        <span>Continue to secure checkout</span>
                      </div>
                    )}
                  </button>

                  <div className="mt-2 flex items-center justify-center gap-2 text-[12px] font-semibold text-brand-pink">
                    <span
                      className="relative h-[25px] w-[24px]"
                      aria-hidden="true"
                    >
                      <ShieldCheck
                        className="absolute inset-0 h-[25px] w-[24px]"
                        fill="#f30099"
                        stroke="#f30099"
                      />
                      <Check
                        className="absolute left-1/2 top-[52%] h-[14px] w-[14px] -translate-x-1/2 -translate-y-1/2 text-white"
                        strokeWidth={3}
                      />
                    </span>
                    <span>Charged monthly • Cancel anytime</span>
                  </div>

                  {/* ReCAPTCHA Components */}
                  {showChallenge ? (
                    <div className="mt-4 flex justify-center">
                      <ReCAPTCHA
                        ref={recaptchaChallengeRef}
                        sitekey={RECAPTCHA_CHECKBOX_KEY}
                      />
                    </div>
                  ) : (
                    <ReCAPTCHA
                      ref={recaptchaScoreRef}
                      size="invisible"
                      sitekey={RECAPTCHA_SCORE_KEY}
                      badge="bottomright"
                    />
                  )}

                  {/* Trust Footer */}
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <p className="text-[10px] text-gray-500 leading-relaxed text-center">
                      This site is protected by reCAPTCHA and the{" "}
                      <a
                        href="https://policies.google.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-pink hover:underline"
                      >
                        Google Privacy Policy
                      </a>{" "}
                      and{" "}
                      <a
                        href="https://policies.google.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-pink hover:underline"
                      >
                        Terms of Service
                      </a>{" "}
                      apply.
                    </p>
                  </div>
                </form>
              </div>
            </section>
          )}

          {/* SECTION 1 — CLARITY FIRST */}
          <section id="how-lexi-works" className="pt-10 xl:pt-20">
            <div className="text-center px-5">
              <h2 className="lexi-pro-section-title text-[24px] xl:text-[32px] font-extrabold text-black">
                How Lexi Pro Works
              </h2>
              <p className="lexi-pro-body mt-2 text-[14px] xl:text-[18px] leading-relaxed text-gray-600">
                Tips delivered across the full race lifecycle.
              </p>
            </div>

            <div className="mt-6 xl:mt-10 grid grid-cols-1 xl:grid-cols-3 gap-6">
              {[
                {
                  key: "early",
                  title: "Early Mail",
                  badge: true,
                  bullets: [
                    {
                      text: "Early Mail lands up to 3 days out (Saturday's can arrive on Wednesday), so you can plan your day early.",
                    },
                    { text: "Includes Lexi's odds and race analysis." },
                  ],
                  gallery: earlyMailGallery,
                  galleryClass: "early-mail-swiper",
                },
                {
                  key: "race",
                  title: "Race Day Tips",
                  badge: false,
                  bullets: [
                    {
                      text: "Refined selections on race morning as form, conditions and markets firm.",
                    },
                    {
                      text: "Stay on top of track changes, scratchings, and market moves.",
                    },
                  ],
                  gallery: raceDayGallery,
                  galleryClass: "race-day-swiper",
                },
                {
                  key: "late",
                  title: "Late Mail",
                  badge: false,
                  bullets: [
                    {
                      text: "Race and market is analysed 3-5 minutes before jump.",
                    },
                    {
                      text: "Adjustments for late scratchings, rider changes, and market shifts.",
                    },
                    { text: "Late market movers.", icon: "chevronsUp" },
                    { text: "Race-day tip adjustments.", icon: "chevronUp" },
                    { text: "No movement in Late Mail.", icon: "circle" },
                  ],
                  gallery: lateMailGallery,
                  galleryClass: "late-mail-swiper",
                },
              ].map((section) => (
                <div
                  key={section.key}
                  className="how-section rounded-2xl border border-gray-200 pt-6 shadow-sm flex flex-col"
                >
                  <div className="min-w-0 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 text-[20px] xl:text-[22px] font-extrabold text-black mb-3 px-5">
                      <span>{section.title}</span>
                      {section.badge ? (
                        <span className="relative inline-flex items-center h-[18px]">
                          <span
                            className="absolute inset-0 rounded-full bg-emerald-200 opacity-60"
                            aria-hidden="true"
                          />
                          <span className="relative inline-flex items-center rounded-full bg-white px-2 py-[2px] text-[10px] font-black uppercase tracking-[0.5px] text-emerald-600 shadow-[0_0_10px_rgba(16,185,129,0.45)] animate-pulse">
                            NEW
                          </span>
                        </span>
                      ) : null}
                    </div>
                    <ul className="mt-1 space-y-1 text-[13px] xl:text-[14px] leading-relaxed text-gray-600 list-none px-5">
                      {section.bullets.map((item) => {
                        const iconNode = item.icon
                          ? bulletIcons[item.icon]
                          : null;
                        return (
                          <li
                            key={item.text}
                            className={
                              iconNode ? "flex items-start gap-2" : "block"
                            }
                          >
                            {iconNode ? (
                              <span className="inline-flex w-5 justify-center flex-shrink-0 pt-[2px]">
                                {iconNode}
                              </span>
                            ) : null}
                            <span>{item.text}</span>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="flex-1" />
                    {section.gallery ? (
                      <Swiper
                        modules={[Autoplay, Pagination, A11y]}
                        spaceBetween={0}
                        slidesPerView={1}
                        centeredSlides={false}
                        loop
                        grabCursor
                        autoplay={{
                          delay: 3000,
                          disableOnInteraction: false,
                          pauseOnMouseEnter: true,
                        }}
                        pagination={{ clickable: true }}
                        a11y={{ enabled: true }}
                        className={`mt-4 ${section.galleryClass} !pb-6 cursor-grab active:cursor-grabbing`}
                      >
                        {section.gallery.map((image) => (
                          <SwiperSlide key={image.src}>
                            <div className="overflow-hidden rounded-xl border border-gray-100 shadow-sm cursor-grab active:cursor-grabbing">
                              <img
                                src={image.src}
                                alt={image.alt}
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            </div>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 xl:mt-10 flex justify-center">
              <LexiProCtaButton
                onClick={scrollToSignupForm}
                className="w-full max-w-[360px] xl:max-w-[400px] border-[6px] border-[#FCE7F5] rounded-[999px] text-[15px] xl:text-[17px] font-[600]"
              >
                Start Lexi Pro $29.95/mth
              </LexiProCtaButton>
            </div>
          </section>

          {/* SECTION 2 — PRO POSITIONING */}
          <section id="why-lexi-pro" className="pt-10 xl:pt-16">
            <div className="lexi-pro-card rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="grid gap-5 xl:grid-cols-2 xl:gap-10">
                <div>
                  <h2 className="lexi-pro-section-title text-[20px] font-extrabold  text-black">
                    Designed for Serious <br />
                    Racing Days
                  </h2>
                  <p className="lexi-pro-body mt-2 text-[14px] leading-relaxed text-gray-600">
                    Lexi Pro isn’t about chasing every race - it’s about
                    clarity, timing and discipline.
                  </p>
                  <p className="lexi-pro-body mt-3 text-[14px] leading-relaxed text-gray-700">
                    Lexi Pro helps narrow the field, reduce noise, and structure
                    race days with less guesswork. It’s built for people who want
                    consistency, not hype.
                  </p>

                  <button
                    type="button"
                    onClick={scrollToSignupForm}
                    className="mt-4 inline-flex text-[14px] font-semibold text-brand-pink underline underline-offset-4"
                  >
                    Unlock full Lexi Pro access →
                  </button>
                </div>

                <div className="rounded-2xl bg-gray-50 p-4 border border-gray-200">
                  <ul className="grid gap-3">
                    <li className="flex items-start gap-2">
                      <Check
                        className="mt-[2px] h-5 w-5 text-brand-pink"
                        aria-hidden="true"
                      />
                      <span className="text-[13px] leading-relaxed text-gray-700">
                        Up to 12 AI-generated tips per race <br />
                        Early Mail, Race-Day &amp; Late Mail
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check
                        className="mt-[2px] h-5 w-5 text-brand-pink"
                        aria-hidden="true"
                      />
                      <span className="text-[13px] leading-relaxed text-gray-700">
                        Every Australian track, every race and day
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check
                        className="mt-[2px] h-5 w-5 text-brand-pink"
                        aria-hidden="true"
                      />
                      <span className="text-[13px] leading-relaxed text-gray-700">
                        Fluctuations and market movers in Late Mail
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check
                        className="mt-[2px] h-5 w-5 text-brand-pink"
                        aria-hidden="true"
                      />
                      <span className="text-[13px] leading-relaxed text-gray-700">
                        No manual tipsters or opinion bias
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 3 — LEXICHAT (INTERACTION) */}
          <section id="lexichat" className="pt-10 xl:pt-16">
            <div className="lexi-pro-card rounded-2xl border border-gray-200 bg-white p-5 shadow-sm xl:max-w-[800px] xl:mx-auto">
              <div className="flex items-start">
                <div className="min-w-0">
                  <h2 className="lexi-pro-section-title text-[20px] font-extrabold  text-black">
                    Meet LexiChat
                    <MessageCircle
                      className="inline h-5 w-5 text-brand-pink"
                      style={{ marginBottom: "4px", marginLeft: "0.5em" }}
                      aria-hidden="true"
                    />
                  </h2>
                  <p className="lexi-pro-body mt-2 text-[14px] leading-relaxed text-gray-600">
                    Your AI racing assistant, available anytime.
                  </p>
                  <p className="lexi-pro-body mt-3 text-[14px] leading-relaxed text-gray-700">
                    LexiChat lets you ask questions about runners, form,
                    conditions and today’s races - all within the app.
                  </p>
                </div>
              </div>

              <div className="mt-4 rounded-2xl bg-gray-50 p-4 border border-gray-200">
                <div className="text-[12px] font-bold tracking-wide text-gray-700 uppercase">
                  Example prompts
                </div>
                <ul className="mt-3 grid gap-2">
                  <li className="text-[13px] text-gray-700">
                    “Which horse has the best jockey?”
                  </li>
                  <li className="text-[13px] text-gray-700">
                    “Who has run this distance before?”
                  </li>
                  <li className="text-[13px] text-gray-700">
                    “Which horse is the best outsider?”
                  </li>
                </ul>
              </div>

              <div className="mt-5">
                <LexiProCtaButton
                  onClick={scrollToSignupForm}
                  className="w-full rounded-[999px] border-[6px] border-[#FCE7F5] text-[15px] font-[600]"
                >
                  Start Lexi Pro $29.95/mth
                </LexiProCtaButton>
              </div>
            </div>
          </section>

          {/* SECTION 4 — TRUST & TRANSPARENCY */}
          <section id="results-history" className="pt-10 xl:pt-16">
            <div className="lexi-pro-card rounded-2xl border border-gray-200 bg-white p-5 shadow-sm xl:max-w-[900px] xl:mx-auto">
              <div className="flex items-start gap-3">
                <div className="mt-[2px] inline-flex h-10 w-10 items-center justify-center rounded-xl">
                  <ShieldCheck
                    className="h-5 w-5 text-brand-pink"
                    aria-hidden="true"
                  />
                </div>
                <div className="min-w-0">
                  <h2 className="lexi-pro-section-title text-[20px] font-extrabold  text-black">
                    Transparent Results History
                  </h2>
                  <p className="lexi-pro-body mt-2 text-[14px] leading-relaxed text-gray-600">
                    Every race tracked. Nothing hidden.
                  </p>
                  <p className="lexi-pro-body mt-3 text-[14px] leading-relaxed text-gray-700">
                    Lexi Pro includes full historical results from February 12,
                    2025 onwards, so users can review performance over time.
                  </p>
                </div>
              </div>

              <ul className="mt-4 grid gap-2">
                <li className="flex items-start gap-2">
                  <Check
                    className="mt-[2px] h-5 w-5 text-brand-pink"
                    aria-hidden="true"
                  />
                  <span className="text-[13px] leading-relaxed text-gray-700">
                    No deleted races
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check
                    className="mt-[2px] h-5 w-5 text-brand-pink"
                    aria-hidden="true"
                  />
                  <span className="text-[13px] leading-relaxed text-gray-700">
                    No selective highlighting
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check
                    className="mt-[2px] h-5 w-5 text-brand-pink"
                    aria-hidden="true"
                  />
                  <span className="text-[13px] leading-relaxed text-gray-700">
                    Results available to all Pro users
                  </span>
                </li>
              </ul>

              <button
                type="button"
                onClick={scrollToSignupForm}
                className="mt-4 inline-flex text-[14px] font-semibold text-brand-pink underline underline-offset-4"
              >
                Access full history with Lexi Pro →
              </button>

              <div className="mt-5">
                <img
                  src="/images/gallery/results/lexi-results-page-1.png"
                  alt="Lexi Pro transparent results history preview"
                  className="w-full rounded-xl border border-gray-200 shadow-sm"
                  loading="lazy"
                />
              </div>
            </div>
          </section>

          {/* SECTION 5 — SOCIAL PROOF */}
          <div
            ref={reviewsRef}
            id="reviews"
            className="pt-10 xl:pt-16 xl:max-w-[1000px] xl:mx-auto"
          >
            <LexiGoogleReviewsSection
              id="reviews-section"
              title="Trusted by Punters Across Australia"
              subtitle="Used daily by beginners and long-time racing followers."
              reviews={LEXI_GOOGLE_REVIEWS}
              afterReviews={
                <p className="text-[12px] text-gray-500 text-center">
                  Real users. Real experiences.
                </p>
              }
            />
          </div>

          {/* SECTION 6 — PRIMARY CONVERSION */}
          <section id="primary-cta" className="pt-10 xl:pt-16">
            <div className="lexi-pro-card rounded-2xl border border-gray-200 bg-white p-5 shadow-sm xl:max-w-[700px] xl:mx-auto">
              <div className="text-center">
                <h2 className="lexi-pro-section-title text-[22px] font-extrabold  text-black">
                  Start Using Lexi Pro
                </h2>
                <p className="lexi-pro-body mt-2 text-[14px] leading-relaxed text-gray-600">
                  Full access. Every track. Every race.
                </p>
              </div>

              <div className="mt-5">
                <LexiProCtaButton
                  onClick={scrollToSignupForm}
                  className="w-full rounded-[999px] border-[7px] border-[#FCE7F5] text-[16px] font-[700]"
                >
                  Start Lexi Pro $29.95/month
                </LexiProCtaButton>
              </div>

              <div className="mt-3 flex items-center justify-center gap-2">
                <span className="relative h-[25px] w-[24px]" aria-hidden="true">
                  <ShieldCheck
                    className="absolute inset-0 h-[25px] w-[24px]"
                    fill="#f30099"
                    stroke="#f30099"
                  />
                  <Check
                    className="absolute left-1/2 top-[52%] h-[14px] w-[14px] -translate-x-1/2 -translate-y-1/2 text-white"
                    strokeWidth={3}
                  />
                </span>
                <span className="text-[14px] font-semibold leading-[29px] text-brand-pink">
                  Cancel anytime
                </span>
              </div>
            </div>
          </section>

          {/* SECTION 7 — SOFT FALLBACK (LEXI SOCIAL) */}
          <section id="lexi-social" className="hidden pt-10">
            <div className="lexi-pro-card rounded-2xl border border-gray-200 bg-gray-50 p-5">
              <h2 className="lexi-pro-section-title text-[18px] font-extrabold text-black">
                Just Getting Started?
              </h2>
              <p className="lexi-pro-body mt-2 text-[13px] leading-relaxed text-gray-600">
                If you’d like to explore Lexi before upgrading, you can start
                with Lexi Social at no cost.
              </p>

              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => navigate("/signup")}
                  className={[
                    "w-full rounded-xl py-3 font-semibold",
                    "border border-gray-300 bg-white text-gray-900",
                    "hover:bg-gray-50 active:scale-[0.99]",
                    "transition-transform duration-150 ease-out",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/20",
                  ].join(" ")}
                >
                  Start with Lexi Social ($0)
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Footer (matches home page) */}
      <Footer />
    </div>
  );
}
